// src/pages/users/UserList.tsx
import React, { useMemo, useRef } from 'react';
import { Button } from 'antd';
import { AgGridReact } from 'ag-grid-react';
import { Link, useNavigate } from 'react-router-dom';

import { PLGrid } from '../../components/common/PLGrid';
import { GridResetButton } from '../../components/common/GridResetButton';
import ExportAgCSVButton from '../../components/common/ExportAgCSVButton';
import BulkUploadButton from '../../components/common/BulkUploadButton';
import { getActionColumn } from '../../libs/getActionColumn';
import { serialNumberColumn } from '../../libs/getSerialNumberColumn';
import { useGridData } from '../../hooks/useGridData';
import { useDeleteHandler } from '../../hooks/useDeleteHandler'; 

import { userConfig } from './user.config';
import { User } from '../../types/users.types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faKey } from '@fortawesome/free-solid-svg-icons';

const UserList: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const { rowData, loading, refetch } = useGridData<User>(userConfig.api.fetchAll);
  const deleteHandler = useDeleteHandler();
  const navigate = useNavigate();

  const actionColumn = useMemo(
    () =>
      getActionColumn<User>({
        viewUrl: (row) => `${userConfig.redirectPath}/view/${row._id}`,
        editUrl: (row) => `${userConfig.redirectPath}/edit/${row._id}`,
        onDelete: (row) =>
          deleteHandler({
            id: row._id,
            entityName: userConfig.entityName,
            deleteApi: userConfig.api.delete,
            onSuccess: () => refetch(),
          }),
        additionalActions: (row) => (
          <>
            <Link to={`/accounts/users/login-histories/${row._id}`} className="pl-2" title="View Login History">
              <FontAwesomeIcon
                icon={faHistory}
                className="text-gray-600 hover:text-gray-800 cursor-pointer"
              />
            </Link>
            <Link to={`/accounts/users/password-histories/${row._id}`} className="pl-2" title="View Password Change History">
              <FontAwesomeIcon
                icon={faKey}
                className="text-gray-600 hover:text-gray-800 cursor-pointer"
              />
            </Link>
          </>
        ),
        width: 170,
      }),
    [deleteHandler, refetch]
  );

  const columnDefs = useMemo(
    () => [serialNumberColumn, ...userConfig.userTableColumns, actionColumn],
    [actionColumn]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold m-0">{userConfig.entityName} List</h2>
        <div className="flex items-center gap-2">
          <GridResetButton gridRef={gridRef} />
          <ExportAgCSVButton gridRef={gridRef} fileName={`${userConfig.entityName}List`} />
          <BulkUploadButton redirectPath={`${userConfig.redirectPath}/excel-upload`} />
          <Button type="primary" onClick={() => navigate(`${userConfig.redirectPath}/create`)}>
            Create {userConfig.entityName}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto">
        <PLGrid ref={gridRef} rowData={rowData} columnDefs={columnDefs} loading={loading} />
      </div>
    </div>
  );
};

export default UserList;
